import {useMemo} from 'react';

import styled, {keyframes} from 'styled-components';

import {DeliveryTrackerJobStatuses, SortingDeliveryTrackerJobStatusesOrder, DeliveryTrackerBarStatuses} from '~/shared/consts/deliveryTrackerConsts';
import {getLocalizationService} from '~/shared/services/localisationService';
import {flexColumn} from '~/shared/theme/FlexLayout';
import {media} from '~/shared/theme/media';
import {body12Normal, body14Normal} from '~/shared/theme/typography';
import {Section} from '~/shared/components/OrderCompleteSections/parts';

const DeliveryTrackerDetailsWrapper = styled(Section)<{
  mapDisplayed?: boolean;
  isShowDeliveryTracker?: boolean;
}>`
  padding: 16px 0;
  margin-top: ${({mapDisplayed}) => (mapDisplayed ? '400px' : '593px')};
  ${media.minLargeDesktop`
    margin-top: ${({mapDisplayed, isShowDeliveryTracker}) => (mapDisplayed ? 450 : isShowDeliveryTracker ? 593 : 561)}px;
  `};
`;

const DeliveryTrackerStatusContainer = styled.div`
  width: 100%;
  display: flex;
  justify-content: space-around;
`;
const DeliveryTrackerStatusBarWrapper = styled.div`
  ${flexColumn};
  width: 75px;
  ${media.minLargeTablet`
    width: 122px;
 `}
`;

const StatusText = styled.div<{
  effect: DeliveryTrackerBarStatuses;
}>`
  color: ${({theme, effect}) =>
    (effect === DeliveryTrackerBarStatuses.FINISHED || effect === DeliveryTrackerBarStatuses.LOADING
      ? theme.colors.primary
      : theme.colors.background)};
  ${body12Normal};
  text-align: center;
  margin-top: 5px;
  white-space: nowrap;
  ${media.minLargeTablet`
    text-align: center;
    width: auto;
 `}
  ${media.minIphoneX`
    ${body14Normal};
 `}
`;

const animationKeyFramesRTL = keyframes`
  0% {
    right: -100%;
  }

  100% {
    right: 100%;
  }
`;

const animationKeyFramesLTR = keyframes`
  0% {
    right: 100%;
  }

  100% {
    right: -100%;
  }
`;

const DeliveryTrackerStatusBar = styled.div<{
  effect: DeliveryTrackerBarStatuses;
  jobStatus?: DeliveryTrackerJobStatuses;
}>`
  border-radius: 5px;
  height: 5px;
  background-color: ${({theme, effect, jobStatus}) =>
    (effect === DeliveryTrackerBarStatuses.FINISHED ||
    (effect === DeliveryTrackerBarStatuses.LOADING && jobStatus === DeliveryTrackerJobStatuses.EatingTime)
      ? theme.colors.primary
      : theme.colors.background)};
  position: relative;
  overflow: hidden;

  &::after {
    content: '';
    position: absolute;
    width: ${({effect}) => (effect === DeliveryTrackerBarStatuses.LOADING ? '50%' : '0')};
    height: 100%;
    background-color: ${({theme, effect}) =>
    (effect === DeliveryTrackerBarStatuses.FINISHED || effect === DeliveryTrackerBarStatuses.LOADING
      ? theme.colors.primary
      : theme.colors.background)};
    right: -100%;
    animation: ${({theme}) => (theme.isLTR ? animationKeyFramesLTR : animationKeyFramesRTL)} 1s
      ${({effect}) => (effect === DeliveryTrackerBarStatuses.LOADING ? 'infinite' : '0')};
  }
`;

const StatusBlock = ({jobStatusIndex, effect}: {
  jobStatusIndex: number;
  effect: DeliveryTrackerBarStatuses;
}) => {
  const {t} = getLocalizationService();

  const jobStatus = (Object.keys(SortingDeliveryTrackerJobStatusesOrder) as DeliveryTrackerJobStatuses[]).find(
    k => SortingDeliveryTrackerJobStatusesOrder[k] === jobStatusIndex,
  );
  const isCancelledStatus = jobStatus && DeliveryTrackerJobStatuses[jobStatus] === DeliveryTrackerJobStatuses.Canceled;

  if (isCancelledStatus) {
    return null;
  }

  return (
    <DeliveryTrackerStatusBarWrapper key={jobStatus}>
      <DeliveryTrackerStatusBar key={jobStatus} effect={effect} jobStatus={jobStatus} />
      <StatusText effect={effect}>{t(jobStatus ? DeliveryTrackerJobStatuses[jobStatus].toLowerCase() : '')}</StatusText>
    </DeliveryTrackerStatusBarWrapper>
  );
};

const calculateEffects = (currentRelevantStatus?: DeliveryTrackerJobStatuses) => {
  if (!currentRelevantStatus || currentRelevantStatus === DeliveryTrackerJobStatuses.Canceled) {
    return Array(Object.keys(DeliveryTrackerJobStatuses).length).fill(DeliveryTrackerBarStatuses.ON_HOLD);
  }

  return (Object.keys(DeliveryTrackerJobStatuses) as DeliveryTrackerJobStatuses[]).map(status => {
    const currentStatusCounter = SortingDeliveryTrackerJobStatusesOrder[currentRelevantStatus];
    const statusCounter = SortingDeliveryTrackerJobStatusesOrder[status];

    if (statusCounter < currentStatusCounter) {
      return DeliveryTrackerBarStatuses.FINISHED;
    }

    if (statusCounter === currentStatusCounter) {
      return DeliveryTrackerBarStatuses.LOADING;
    }

    return DeliveryTrackerBarStatuses.ON_HOLD;
  });
};

const DeliveryTrackerStatus = ({status: currentRelevantStatus, mapDisplayed, isShowDeliveryTracker}: {
  status?: DeliveryTrackerJobStatuses;
  mapDisplayed?: boolean;
  isShowDeliveryTracker: boolean;
}) => {
  const effects = useMemo(() => calculateEffects(currentRelevantStatus), [currentRelevantStatus]);

  return (
    // @ts-expect-error Section.js should be refactored to ts
    <DeliveryTrackerDetailsWrapper mapDisplayed={mapDisplayed} isShowDeliveryTracker={isShowDeliveryTracker}>
      <DeliveryTrackerStatusContainer>
        {effects.map((effect, index) => (
          <StatusBlock key={`delivery_status_block_${effect.toString() + index}`} jobStatusIndex={index} effect={effect} />
        ))}
      </DeliveryTrackerStatusContainer>
    </DeliveryTrackerDetailsWrapper>
  );
};
export default DeliveryTrackerStatus;
